import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback
} from "react";
import { FaBackward, FaSpinner } from "react-icons/fa";
import { Link, useSearchParams } from "react-router-dom";
import {
  agreementMailPages,
  agreementTexts,
  buttonName,
  commonErrMsg,
  commonValidationMsg,
  identityText,
  mail,
  optimizationTexts,
  ownerTab,
  pagination,
  tooltips
} from "../../../locales/en";
import MultiStepFormContext from "../../../components/MultiStepForm/MultiStepFormContext";
import publicAxios from "../../../utils/publicAxios";
import { showToast } from "../../../utils/utils";
import {
  ADDRESSFINDERKEY,
  MAILTO_INFO_EMAIL,
  MAX_CALL_COUNT,
  PROPERTY_TYPE,
  SCHEDULED_SECOND_FIVE,
  SCHEDULED_SECOND_TEN,
  showToastType
} from "../../../utils/constants/appConstants";
import { FILE_SIZE_LIMIT } from "../../../utils/constants/appConstants";
import {
  getAccAnnatureStatusURL,
  getAgreementUploadUrlLaterIDTokenUrl,
  getCountryCodeUrl,
  getLeagalAgreementDownloadTokenUrl,
  getLeagalAgreementUploadTokenUrl,
  getOnboardGuaranteeFormURL,
  getUpdateOnboardStepsTokenUrl
} from "../../../utils/constants/apiEndPoints";
import { WidgetInput } from "react-addressfinder";
import { getOwnPropertyOptions } from "../../../utils/getDropdownOptions";
import { useSelector } from "react-redux";
import AgreementModalId from "../../../components/modals/AgreementModalNoID";

const Agreement = () => {
  const { prev } = useContext(MultiStepFormContext);
  const [showManually, setShowManually] = useState();
  const [showSubmit, setSubmit] = useState();
  const [addAgreementModal, setAddAgreementModal] = useState(false);
  const [searchParams] = useSearchParams();

  const [getToken, setGetToken] = useState("");
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [isUploadLater, setIsUploadLater] = useState(false);
  const fileInput = useRef(null);
  const { applyIdDataToken } = useSelector((state) => state.agreementPages);
  const [annatureStatusData, setAnnatureStatusData] = useState(null);
  const [data, setData] = useState(null);
  const [isDisabledButton, setIsDisabledButton] = useState(false);
  const [anatureDataUrl, setAnatureDataUrl] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [anatureLoading, setAnatureLoading] = useState(false);

  const handleDownload = async (event) => {
    if (event) event.preventDefault();
    try {
      setLoading(true);
      const response = await publicAxios.get(
        getLeagalAgreementDownloadTokenUrl,
        {
          responseType: "arraybuffer",
          params: {
            token: getToken
          }
        }
      );

      // Create a Blob from the response data
      const blob = new Blob([response.data], { type: "application/pdf" });

      // Create a link element
      const link = document.createElement("a");

      // Set the download attribute with the filename from the response headers
      const filename = "FundTap Deed.pdf";
      link.href = URL.createObjectURL(blob);
      link.download = filename;

      // Trigger the download by simulating a click on the link
      link.click();

      // Clean up the object URL
      URL.revokeObjectURL(link.href);
    } catch {
      showToast(commonErrMsg.item11, showToastType.error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (event) => {
    const { files } = event.target;

    // Ensure a file is selected
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];

    if (file.size > FILE_SIZE_LIMIT) {
      showToast(commonValidationMsg?.fileLimitError, showToastType.error);
      event.target.value = "";
      setFileName("");
      return;
    }

    setFileName(file.name);

    try {
      setLoadingUpload(true);

      const formDataToSend = new FormData();
      formDataToSend.append("file", file, file.name);

      const config = {
        method: "post",
        url: getLeagalAgreementUploadTokenUrl,
        headers: {
          "Content-Type": "multipart/form-data"
        },
        params: {
          token: getToken
        },
        data: formDataToSend
      };

      const response = await publicAxios(config);

      if (response.status === 200) {
        showToast(
          response.data.message ?? optimizationTexts?.fileUploaded,
          showToastType.success
        );
      }
    } catch (error) {
      if (error.response) {
        showToast(error.response.data.message, showToastType.error);
      }
    } finally {
      setLoadingUpload(false);
    }
  };

  const handleUploadButtonClick = () => {
    fileInput.current.click();
  };

  const handleCheckboxChange = async (e) => {
    const { checked, type, value } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setIsUploadLater(newValue);

    try {
      const response = await publicAxios.post(
        getAgreementUploadUrlLaterIDTokenUrl,
        {},
        {
          params: {
            isUploadLater: newValue,
            token: getToken
          }
        }
      );

      if (response.status === 200) {
        showToast(response.data.message, showToastType.success);
      }
    } catch (error) {
      showToast(error.response.data.message, showToastType.error);
    }
  };

  const toggleShowManually = () => {
    setShowManually(!showManually);
    setSubmit(true);
  };

  const handleBackToDigitalClick = () => {
    setShowManually(false);
    setSubmit(false);
  };

  const [manualAddress, setManualAddress] = useState("");
  const [showManualInput, setShowManualInput] = useState(false);

  const handleOnChange = async (e) => {
    const { value } = e.target;

    if (value === "Yes" || value === "No") {
      setManualAddress("");
      setShowManualInput(false);
    } else {
      setShowManualInput(true);
    }

    if (value === PROPERTY_TYPE) {
      return;
    }

    if (manualAddress.trim() !== "") {
      setShowManualInput(false);
    }

    try {
      const payload = {
        propertyOwnership: value
      };

      await publicAxios.put(getUpdateOnboardStepsTokenUrl, payload, {
        params: { token: getToken }
      });
    } catch {
      // handle catch here;
    }
  };

  const handleManualChange = (e) => {
    setManualAddress(e.target.value);
  };

  const [successMsg, setSuccessMsg] = useState(false);

  const handleConfirm = () => {
    setSuccessMsg(true);
  };

  const handleSelectSuggestionAddress = async (address) => {
    try {
      const payload = {
        propertyOwnership: address
      };

      await publicAxios.put(getUpdateOnboardStepsTokenUrl, payload, {
        params: { token: getToken }
      });
    } catch {
      // handle catch here;
    }
  };

  // Country Code Fetch=====>
  const [countryCode, setCountryCode] = useState("");

  const getCountryCode = useCallback(async (getToken) => {
    try {
      const response = await publicAxios.get(getCountryCodeUrl, {
        params: { token: getToken }
      });
      setCountryCode(response?.data.data.primaryCountry);
    } catch {
      // handle catch here;
    }
  }, []);

  const loadGuranteeData = useCallback(async (getToken) => {
    try {
      setAnatureLoading(true);
      const response = await publicAxios.post(
        getOnboardGuaranteeFormURL,
        {},
        {
          params: {
            token: getToken
          }
        }
      );
      setData(response.data.data);
      setAnatureDataUrl(
        response?.data?.data?.signing_token_links?.[0]?.endpoint
      );
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || identityText?.tokenExpiredText
      );
    } finally {
      setAnatureLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = searchParams.get("token");
    setGetToken(token);
    getCountryCode(token);
    loadGuranteeData(token);
  }, [searchParams]);

  const showAgreementModal = () => {
    if (addAgreementModal) {
      let callCount = 0;
      setIsDisabledButton(true);
      const scheduleNextCall = (delay) => {
        setTimeout(() => {
          callCount++;
          setIsDisabledButton(false);
          if (callCount < MAX_CALL_COUNT) {
            scheduleNextCall(SCHEDULED_SECOND_FIVE);
          }
        }, delay);
      };
      scheduleNextCall(SCHEDULED_SECOND_TEN);
    }
    setAddAgreementModal(!addAgreementModal);
  };

  const handleAnnatureCheckStatus = useCallback(async () => {
    try {
      const response = await publicAxios.post(getAccAnnatureStatusURL, null, {
        params: { userAccountId: data?.user_account_id || data?.id }
      });
      if (response.status === 200) {
        const responseData = response.data.data;
        setAnnatureStatusData(responseData);
      }
    } catch (error) {
      showToast(error.response?.data?.message, showToastType.error);
    }
  }, [data]);

  useEffect(() => {
    if (data?.user_account_id || data?.id) {
      handleAnnatureCheckStatus();
    }
  }, [handleAnnatureCheckStatus, data]);

  const renderAgreementActions = () => {
    if (annatureStatusData && annatureStatusData.status) {
      return <p className="text-success">{annatureStatusData?.displayText}</p>;
    }

    if (errorMessage) {
      return (
        <div className="expired-token-message text-center pt-5">
          <h3>{identityText?.tokenExpiredText}</h3>
        </div>
      );
    }

    return (
      <>
        <button
          type="button"
          className="btn btn-primary"
          onClick={showAgreementModal}
          disabled={isDisabledButton}
        >
          {agreementTexts?.reviewSign}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-transparent"
          onClick={toggleShowManually}
          disabled={isDisabledButton}
        >
          {agreementTexts?.signUpload}
        </button>
      </>
    );
  };

  if (successMsg) {
    return (
      <div className="alert alert-success mt-5 text-center" role="alert">
        {`${agreementMailPages?.addOwners.successText} `}
        <a href={MAILTO_INFO_EMAIL}>{mail}</a>
      </div>
    );
  }

  return (
    <>
      <div className="row">
        <div className="col-md-8 offset-md-2">
          <div className="step-wrap">
            <div className="step-title">
              <h3>{agreementTexts?.tcGuarntee.title}</h3>
            </div>
            {applyIdDataToken?.status && (
              <div className="doYouOwn mt-3 row ">
                <div className="col-md-6 mb-3">
                  <label htmlFor="select" className="ms-1">
                    {agreementTexts?.tcGuarntee.doYouOwn}
                  </label>
                  <span className="text-danger">*</span> <br />
                </div>
                <div className="col-md-6 addowners-form">
                  <div className="input-wrap">
                    <select id="select" onChange={handleOnChange}>
                      <option value="">{ownerTab?.ownerDetails.choose}</option>
                      {getOwnPropertyOptions()?.map((item, index) => (
                        <option value={item.value} key={index}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {showManualInput && (
                  <>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="select" className="ms-1">
                        {ownerTab?.proprtyDetails.propertyOwnedAddrs}
                      </label>
                      <span className="text-danger">*</span> <br />
                    </div>
                    <div className="col-md-6 addowners-form">
                      <div className="input-wrap">
                        <WidgetInput
                          addressFinderKey={ADDRESSFINDERKEY}
                          id="propertyAddress"
                          country={countryCode}
                          name="propertyAddress"
                          onChange={handleManualChange}
                          onSelected={(address) => {
                            handleSelectSuggestionAddress(address);
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="step-content mt-3">
              <div
                className={`${showManually ? "hide" : "show"} hide-manually`}
              >
                <div className="aplyid-section">
                  <p>{agreementTexts?.pleaseReview}</p>
                </div>

                <div className="agreement-btn-group mb-3">
                  {renderAgreementActions()}
                </div>
              </div>
              {showManually && (
                <div className="show-manually-wrap mt-5">
                  <div className="show-manual-btn mb-5">
                    <button
                      type="button"
                      className="btn btn-primary btn-transparent"
                      onClick={handleBackToDigitalClick}
                    >
                      <FaBackward /> {agreementTexts?.reviewSign}
                    </button>
                  </div>
                  <div className="aplyid-section">
                    <p>{agreementTexts?.pleaseDownload}</p>
                    <p>{agreementTexts?.youComplete}</p>

                    <div className="agreement-group-warp mt-5">
                      <div className="row mb-2">
                        <div className="col-md-6 d-flex pt-2 pb-2 align-items-center">
                          <label htmlFor="Download">
                            {agreementTexts?.downloadAgreement}
                          </label>
                        </div>
                        <div className="col-md-6">
                          <Link
                            className={`btn btn-primary fundtap_agreement spinDiv`}
                            id="Download"
                            onClick={handleDownload}
                          >
                            {buttonName.download}
                            {loading && (
                              <span className="loadSpin">
                                <FaSpinner />
                              </span>
                            )}
                          </Link>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6 d-flex pt-2 pb-2 align-items-center">
                          <label htmlFor="uploadAgreement">
                            {agreementTexts?.uploadAgreement}
                          </label>
                        </div>
                        <div className="col-md-6">
                          <div className="upload-file">
                            {fileName && (
                              <p className="uploaded-file-name">{fileName}</p>
                            )}
                            <button
                              type="button"
                              onClick={handleUploadButtonClick}
                              className={`btn btn-primary fundtap_agreement spinDiv`}
                            >
                              {tooltips.upload}
                              {loadingUpload && (
                                <span className="loadSpin">
                                  <FaSpinner />
                                </span>
                              )}
                            </button>
                            <input
                              name="isFile"
                              type="file"
                              ref={fileInput}
                              onChange={handleFileChange}
                              className="d-none"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6 offset-md-6">
                          <div className="upload-checkbox">
                            <label htmlFor="upload">
                              {buttonName?.uploadLater}
                            </label>
                            <input
                              type="checkbox"
                              id="upload"
                              checked={isUploadLater}
                              value={isUploadLater}
                              onChange={handleCheckboxChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="step-btn-group d-flex justify-content-center align-items-center mt-5 mb-5">
        <div className="prev-btn p-1">
          <button type="button" className="btn btn-primary" onClick={prev}>
            {pagination.previous}
          </button>
        </div>
        {showSubmit && (
          <div className="next-btn p-1">
            <button
              onClick={handleConfirm}
              type="button"
              className="btn btn-primary"
            >
              {buttonName.submit}
            </button>
          </div>
        )}
      </div>

      <AgreementModalId
        isOpen={addAgreementModal}
        toggle={showAgreementModal}
        anatureDataUrl={anatureDataUrl}
        loading={anatureLoading}
      />
    </>
  );
};

export default Agreement;
